/**
 * Builds Three.js dungeon visuals from generateDungeon output.
 * Adapted from https://github.com/majidmanzarpour/threejs-procedural-dungeon (MIT)
 */
import * as THREE from 'three';
import { PALETTE } from '../../config/Palette.js';
import { makeRng, THEMES, TYPE, TINT, FLOOR, WALL, POOL, delaunay } from './generateDungeon.js';
import { createMaterialKit } from './materialKit.js';
import { createGeometryKit } from './geometryKit.js';

const LIGHT_K = 4 * Math.PI;
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _c = new THREE.Color();
const _Y = new THREE.Vector3(0, 1, 0);
const _E = new THREE.Euler();
const lerpC = (a, b, t) => _c.set(a).lerp(new THREE.Color(b), t).getHex();

function instSet(){
  return { px:[],py:[],pz:[], sx:[],sy:[],sz:[], rx:[],ry:[],rz:[], col:[], delay:[], n:0,
    add(x,y,z, sx,sy,sz, ry, color, delay){
      this.px.push(x); this.py.push(y); this.pz.push(z);
      this.sx.push(sx); this.sy.push(sy); this.sz.push(sz);
      this.rx.push(0); this.ry.push(ry); this.rz.push(0);
      this.col.push(color); this.delay.push(delay); this.n++;
    },
    addT(x,y,z, sx,sy,sz, rx,ry,rz, color, delay){
      this.px.push(x); this.py.push(y); this.pz.push(z);
      this.sx.push(sx); this.sy.push(sy); this.sz.push(sz);
      this.rx.push(rx); this.ry.push(ry); this.rz.push(rz);
      this.col.push(color); this.delay.push(delay); this.n++;
    }};
}
/* shadow: 0 = none, 1 = cast+receive, 2 = receive only */
function buildMesh(set, geo, mat, mode, dur, shadow){
  const alloc = Math.max(set.n,1);
  const mesh = new THREE.InstancedMesh(geo, mat, alloc);
  mesh.count = set.n;
  /* Always allocate an instance-colour buffer, even for the "spare" instances
     past set.n. A shared material rendered by some meshes with instanceColor and
     some without compiles to two program variants and can trip the renderer's
     attribute fast-path; giving every InstancedMesh a colour buffer keeps them
     all on one variant. (Originally a hard r128 crash; cheap insurance since.) */
  for(let i=0;i<alloc;i++) mesh.setColorAt(i, _c.set(i<set.n ? set.col[i] : 0xffffff));
  if(mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  if(shadow===1){ mesh.castShadow = true; mesh.receiveShadow = true; }
  else if(shadow===2) mesh.receiveShadow = true;
  mesh.userData = { set, mode, dur, settled:false };
  writeInstances(mesh, Infinity);
  return mesh;
}
const easeOutCubic = t => 1-Math.pow(1-t,3);
const easeOutBack  = t => { const c=1.70158; return 1 + (c+1)*Math.pow(t-1,3) + c*Math.pow(t-1,2); };
function writeInstances(mesh, t){
  const u = mesh.userData, s = u.set;
  let allDone = true;
  for(let i=0;i<s.n;i++){
    let k = (t - s.delay[i]) / u.dur;
    if(k < 1) allDone = false;
    k = Math.max(0.0001, Math.min(1, k));
    const g = u.mode==='rise' ? easeOutCubic(k) : easeOutBack(k)*Math.min(1,k*8);
    _q.setFromEuler(_E.set(s.rx[i], s.ry[i], s.rz[i]));
    if(u.mode==='rise'){ _p.set(s.px[i], s.py[i], s.pz[i]); _s.set(s.sx[i], s.sy[i]*Math.max(g,0.0001), s.sz[i]); }
    else { const m=Math.max(g,0.0001); _p.set(s.px[i], s.py[i], s.pz[i]); _s.set(s.sx[i]*m, s.sy[i]*m, s.sz[i]*m); }
    _m.compose(_p,_q,_s); mesh.setMatrixAt(i,_m);
  }
  mesh.instanceMatrix.needsUpdate = true;
  u.settled = allDone;
}

export class DungeonBuilder {
  /**
   * @param {object} options
   * @param {THREE.Scene} options.scene
   * @param {number} [options.tileSize=2]
   * @param {number} [options.maxAnisotropy=4]
   * @param {number} [options.maxTorchLights=24]
   * @param {(th: object) => void} [options.onTheme]
   * @param {import('../../config/GameConfig.js').GameConfig['wallCutout']} [options.wallCutout]
   */
  constructor({ scene, tileSize = 2, maxAnisotropy = 4, maxTorchLights = 24, onTheme = null,
                wallCutout = null }) {
    this.scene = scene;
    this.tileSize = tileSize;
    this.maxTorchLights = maxTorchLights;
    this.onTheme = onTheme;
    this.mats = createMaterialKit(maxAnisotropy, wallCutout);
    const geoKit = createGeometryKit();
    this.GEO = geoKit.GEO;
    this._geoKit = geoKit;
    this.group = null;
    this.lightGroup = new THREE.Group();
    this.lightGroup.name = 'DungeonLights';
    this.meshes = {};
    this.lights = [];
    this.fx = { liquids: [], shafts: [], spinners: [], parts: null };
    this.levelGeos = [];
    this.overlay = null;
    this.D = null;
    this.theme = null;
    this.floorColorsBase = null;
    this.floorColorsHeat = null;
    this._elapsed = 0;
    /**
     * Destructible props live apart from the baked dungeon meshes: they are
     * spawned as individual entities by the arena, so their instances must be
     * addressable one at a time (to hide a single broken prop) and sit in a
     * world-scaled group rather than the tile-scaled dungeon group.
     * @type {{ group: THREE.Group, meshes: Record<string, THREE.InstancedMesh> } | null}
     * @private
     */
    this._destruct = null;
    /** @type {Record<string, THREE.BufferGeometry> | null} @private */
    this._destructGeo = null;
    /** @type {Record<string, THREE.Material> | null} @private */
    this._destructMat = null;
  }

  applyThemeEnv(TH) {
    if (this.onTheme) this.onTheme(TH);
    this.mats.matCloth.color.set(TH.cloth);
    this.mats.matIce.color.set(0xb8d8f0);
    this.mats.matMoss.color.set(0x4a7a40);
    this.mats.matBark.color.set(0x5a4632);
    if (TH.pools) {
      this.mats.liquidMat.uniforms.uMode.value = TH.pools.mode;
      this.mats.liquidMat.uniforms.uGlow.value = TH.pools.glow;
      this.mats.liquidMat.uniforms.uColA.value.set(TH.pools.colA);
      this.mats.liquidMat.uniforms.uColB.value.set(TH.pools.colB);
      this.mats.liquidMat.uniforms.uOp.value = TH.pools.mode === 3 ? 0.72 : 1.0;
    }
    this.mats.partMat.uniforms.uKind.value = TH.particles.kind;
    this.mats.partMat.uniforms.uColor.value.set(TH.particles.color);
  }


buildLiquidMesh(cells, wx, wz, y) {
  /* aE = corner-local coords, aM = which of the 4 sides border non-liquid.
     The shader uses both to soften only true edges: single-cell pools get a
     full cooled rim, lake interiors stay seamless. */
  const key = new Set(cells.map(c=>c.x+','+c.y));
  const n = cells.length;
  const pos = new Float32Array(n*18), ae = new Float32Array(n*12), am = new Float32Array(n*24);
  const CE = [-0.5,-0.5, -0.5,0.5, 0.5,0.5, -0.5,-0.5, 0.5,0.5, 0.5,-0.5];
  let o=0, oe=0, om=0;
  for(const c of cells){
    const x0=wx(c.x)-0.51, x1=wx(c.x)+0.51, z0=wz(c.y)-0.51, z1=wz(c.y)+0.51;
    pos.set([x0,y,z0, x0,y,z1, x1,y,z1,  x0,y,z0, x1,y,z1, x1,y,z0], o); o+=18;
    ae.set(CE, oe); oe+=12;
    const mx0 = key.has((c.x-1)+','+c.y) ? 0 : 1, mx1 = key.has((c.x+1)+','+c.y) ? 0 : 1;
    const mz0 = key.has(c.x+','+(c.y-1)) ? 0 : 1, mz1 = key.has(c.x+','+(c.y+1)) ? 0 : 1;
    for(let k=0;k<6;k++){ am[om++]=mx0; am[om++]=mx1; am[om++]=mz0; am[om++]=mz1; }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos,3));
  g.setAttribute('aE', new THREE.BufferAttribute(ae,2));
  g.setAttribute('aM', new THREE.BufferAttribute(am,4));
  this.levelGeos.push(g);
  return new THREE.Mesh(g, this.mats.liquidMat);
}

build(d) {
  this._clearMeshes();
  this.D = d;
  const TH = THEMES[d.params.themeKey];
  this.theme = TH;
  const accC = parseInt(TH.accent.slice(1),16);
  this.applyThemeEnv(TH);
  this.group = new THREE.Group();
  this.group.name = "Dungeon";
  const W=d.W, H=d.H, grid=d.grid, roomId=d.roomId, corridor=d.corridor,
        doorway=d.doorway, bfs=d.bfs, maxBfs=d.maxBfs, rooms=d.rooms,
        lakeMask=d.lakeMask;
  const idx=(x,y)=>y*W+x, wx=x=>x-W/2+0.5, wz=y=>y-H/2+0.5;
  const cellRng = makeRng(d.seed ^ 0x9e3779b9);
  const dStep = 0.016;

  /* moss + pool adjacency masks for floor tinting */
  const mossMask = new Uint8Array(W*H);
  for(const p of d.props) if(p.kind==='moss') mossMask[idx(p.x,p.y)] = 1;
  const poolAdj = (x,y)=>{
    const c=idx(x,y);
    return (x<W-1 && grid[c+1]===POOL) || (x>0 && grid[c-1]===POOL) ||
           (y<H-1 && grid[c+W]===POOL) || (y>0 && grid[c-W]===POOL);
  };

  /* floors */
  const fs = instSet(); this.floorColorsBase=[]; this.floorColorsHeat=[];
  const base = new THREE.Color(), tint = new THREE.Color(),
        heatA = new THREE.Color(0x2f4bb0), heatB = new THREE.Color(0xe8502f);
  for(let y=0;y<H;y++) for(let x=0;x<W;x++){
    const c=idx(x,y); if(grid[c]!==FLOOR || lakeMask[c]) continue;
    let walls8=0;
    for(let oy=-1;oy<=1;oy++) for(let ox=-1;ox<=1;ox++){
      if(!ox&&!oy) continue;
      const nx=x+ox, ny=y+oy;
      if(nx<0||ny<0||nx>=W||ny>=H || grid[idx(nx,ny)]===WALL) walls8++;
    }
    const rid = roomId[c];
    base.set(corridor[c] ? TH.corridor : TH.floor);
    if(rid>=0 && rooms[rid].type!==TYPE.COMBAT) base.lerp(tint.set(TINT[rooms[rid].type]), 0.17);
    if(doorway[c]) base.multiplyScalar(1.14);
    if(mossMask[c]) base.lerp(tint.set(0x4c7a42), 0.32);
    if(TH.pools && TH.pools.mode===0 && poolAdj(x,y)) base.lerp(tint.set(0xff7a33), 0.3);
    base.multiplyScalar(1 - 0.11*Math.min(walls8,4));
    base.multiplyScalar(((x+y)&1) ? 0.965 : 1.0);
    base.multiplyScalar(cellRng.f(0.94,1.06));
    this.floorColorsBase.push(base.getHex());
    const diff = rid>=0 ? rooms[rid].difficulty : (maxBfs ? bfs[c]/maxBfs : 0.5);
    this.floorColorsHeat.push(heatA.clone().lerp(heatB, Math.min(1,diff)).multiplyScalar(0.55 + 0.45*(1-0.09*Math.min(walls8,4))).getHex());
    fs.add(wx(x), cellRng.f(-0.02,0.008), wz(y), 1,1,1, 0, this.floorColorsBase[this.floorColorsBase.length-1], Math.max(0,bfs[c])*dStep);
  }
  this.meshes.floor = buildMesh(fs, this.GEO.floor, this.mats.matStone, 'pop', 0.34, 2);

  /* walls + trim caps */
  const nearFloorBfs = (x,y)=>{ let b=1e4;
    for(let oy=-1;oy<=1;oy++) for(let ox=-1;ox<=1;ox++){
      const nx=x+ox, ny=y+oy;
      if(nx>=0&&ny>=0&&nx<W&&ny<H && bfs[idx(nx,ny)]>=0) b=Math.min(b,bfs[idx(nx,ny)]);
    } return b===1e4?0:b; };
  const ws = instSet(), cs = instSet();
  const wcol = new THREE.Color();
  for(let y=0;y<H;y++) for(let x=0;x<W;x++){
    if(grid[idx(x,y)]!==WALL) continue;
    const h = 2.0 + cellRng.f(-0.25,0.25);
    const dl = nearFloorBfs(x,y)*dStep + 0.30;
    wcol.set(TH.wall).multiplyScalar(cellRng.f(0.9,1.08));
    ws.add(wx(x),0,wz(y), 1,h,1, 0, wcol.getHex(), dl);
    wcol.set(TH.cap).multiplyScalar(cellRng.f(0.92,1.1));
    cs.add(wx(x),h,wz(y), 1,1,1, 0, wcol.getHex(), dl+0.12);
  }
  /* matWall, not matStone: these two are the only meshes tall enough to hide a
     body from the fixed camera, so they are the only ones that open up. */
  this.meshes.wall    = buildMesh(ws, this.GEO.wall, this.mats.matWall, 'rise', 0.42, 1);
  this.meshes.wallCap = buildMesh(cs, this.GEO.wallCap, this.mats.matWall, 'pop', 0.3, 1);

  /* prop instance sets */
  const S = { pillar:instSet(), arch:instSet(), archL:instSet(), torchArm:instSet(),
              flame:instSet(), flameCore:instSet(),
              debrisA:instSet(), debrisB:instSet(), debrisC:instSet(),
              chest:instSet(), chestTrim:instSet(), chestGlow:instSet(),
              grave:instSet(), sarco:instSet(), candle:instSet(), bone:instSet(),
              icicle:instSet(), shardIce:instSet(), roots:instSet(), moss:instSet(),
              crackD:instSet(), skirt:instSet(), bannerRod:instSet(), bannerCloth:instSet(), emblem:instSet(),
              crystal:instSet(), ring:instSet(), plinth:instSet(), platform:instSet(),
              brazier:instSet(), coals:instSet(), basin:instSet(),
              bossGlow:instSet(), bossRock:instSet(),
              cargo:instSet(), cargoMark:instSet(), medBed:instSet(), medScreen:instSet(),
              labTank:instSet(), labTankCap:instSet(), labCore:instSet(),
              planter:instSet(), planterGlow:instSet(),
              consoleB:instSet(), consoleScreen:instSet(), pillarBand:instSet(),
              ship:instSet(), shipGlow:instSet(), serviceCore:instSet(), serviceCoreGlow:instSet() };
  const pd = (x,y)=> Math.max(0,bfs[idx(x,y)])*dStep + 0.62;
  const shaftAt = [];
  let portalXZ = null, runeXZ = null;

  for(const p of d.props){
    const X=wx(p.x), Z=wz(p.y), dl=pd(p.x,p.y);
    switch(p.kind){
      case 'pillar': { const s=p.scale*1.15;
        S.pillar.add(X,0,Z, s,s,s, cellRng.i(0,3)*Math.PI/2, TH.pillar, dl);
        S.pillarBand.add(X,0,Z, s,s,s, 0, accC, dl+0.1); break; }
      case 'debris': { const set=[S.debrisA,S.debrisB,S.debrisC][p.v||0];
        set.add(X,0,Z, p.scale,p.scale*0.85,p.scale, p.rot, lerpC(TH.debris[0],TH.debris[1],cellRng.raw()), dl); break; }
      case 'chest':
        S.chest.add(X,0,Z, 1,1,1, p.rot, 0x8a5a2c, dl);
        S.chestTrim.add(X,0,Z, 1,1,1, p.rot, 0xc8a24a, dl);
        S.chestGlow.add(X,0,Z, 1,1,1, p.rot, 0xffd27a, dl+0.15);
        break;
      case 'shrineCrystal': {
        // The floating "prize" itself is a real weapon pickup entity spawned by
        // DungeonArena (see createPickup/_buildShrineWeapons), not this instanced
        // crystal — only the pedestal and its candles are static dressing here.
        S.plinth.add(X,0,Z, 1,1,1, p.rot, lerpC(TH.pillar,0xffffff,0.12), dl);
        for(let k=0;k<4;k++){
          const a = k*Math.PI/2 + Math.PI/4, cx = X+Math.cos(a)*0.36, cz = Z+Math.sin(a)*0.36;
          S.candle.add(cx, 0.5, cz, 0.8,0.8,0.8, 0, 0xd8cba8, dl+0.15);
          S.flameCore.add(cx, 0.65, cz, 0.5,0.5,0.5, 0, TH.flameCore, dl+0.25);
        }
        shaftAt.push([X,Z,1]);
        break; }
      case 'ring':
        S.platform.add(X,-0.02,Z, 1,1,1, 0, lerpC(TH.floor,0xffffff,0.1), dl);
        S.ring.add(X, 0.16, Z, 1,1,1, 0, 0x3fd0bb, dl+0.1);
        S.pillar.add(X-1.45, 0.1, Z, 0.72,0.72,0.72, 0, TH.pillar, dl+0.15);
        S.pillar.add(X+1.45, 0.1, Z, 0.72,0.72,0.72, 0, TH.pillar, dl+0.15);
        portalXZ = [X,Z];
        shaftAt.push([X,Z,0.9]);
        break;
      case 'bossCrystal': {
        S.bossGlow.add(X,0,Z, 1.15,1.15,1.15, p.rot, 0xff4636, dl);
        S.bossGlow.add(X+0.55,0,Z-0.42, 0.6,0.75,0.6, p.rot+1.2, 0xff6a45, dl+0.12);
        S.bossRock.addT(X-0.62,0,Z+0.42, 0.75,0.8,0.75, 0.05,p.rot+2.1,-0.06, 0x4a3336, dl+0.15);
        S.bossRock.addT(X+0.75,0,Z+0.55, 0.55,0.6,0.55, -0.06,p.rot+3.6,0.05, 0x51383a, dl+0.2);
        S.bossRock.addT(X-0.5,0,Z-0.62, 0.5,0.55,0.5, 0.04,p.rot+4.9,0.04, 0x452f31, dl+0.24);
        const r = rooms[p.roomId];
        runeXZ = {x:X, z:Z, s:Math.min(1.6, Math.max(0.8, (Math.min(r.w,r.h)/2-1.5)/2.3))};
        break; }
      case 'brazier':
        S.brazier.add(X,0,Z, 1,1,1, cellRng.f(0,6.28), 0x3a3f4a, dl);
        S.coals.add(X,0,Z, 1,1,1, 0, 0xff7a30, dl+0.1);
        S.flame.add(X, 0.62, Z, 1.35,1.35,1.35, 0, TH.flame, dl+0.12);
        S.flameCore.add(X, 0.66, Z, 1.3,1.3,1.3, 0, TH.flameCore, dl+0.12);
        break;
      case 'grave':
        S.grave.addT(X,0,Z, p.scale,p.scale,p.scale, cellRng.f(-0.08,0.08), p.rot, cellRng.f(-0.13,0.13),
                     lerpC(TH.wall,0xffffff,0.15), dl);
        break;
      case 'sarco':
        S.sarco.add(X,0,Z, 1,1,1, p.rot, lerpC(TH.pillar,0xffffff,0.08), dl);
        break;
      case 'candle':
        S.candle.add(X,0,Z, p.scale,p.scale,p.scale, 0, 0xd8cba8, dl);
        S.flameCore.add(X, 0.19*p.scale, Z, 0.55,0.55,0.55, 0, TH.flameCore, dl+0.1);
        break;
      case 'icicle':
        S.icicle.add(wx(p.x)+p.dx*0.42, 1.75, wz(p.y)+p.dy*0.42, p.scale,p.scale,p.scale, p.rot,
                     0xbfe2ff, nearFloorBfs(p.x,p.y)*dStep + 0.7);
        break;
      case 'shardIce':
        S.shardIce.addT(X,-0.1,Z, p.scale,p.scale,p.scale, cellRng.f(-0.15,0.15), p.rot, cellRng.f(-0.15,0.15),
                        0xcfeaff, dl);
        break;
      case 'roots':
        S.roots.add(wx(p.x), 0, wz(p.y), p.scale,p.scale,p.scale, Math.atan2(p.dx,p.dy),
                    0x5a4632, nearFloorBfs(p.x,p.y)*dStep + 0.6);
        break;
      case 'moss':
        S.moss.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, lerpC(0x3f6b3a,0x5a8a4a,cellRng.raw()), dl);
        break;
      case 'crack': {
        /* centered on the pool/lake edge so branches radiate outward */
        const cx = X - (p.dx||0)*0.5, cz = Z - (p.dy||0)*0.5;
        const vc = p.ice ? 0x9fd8ff : (TH.pools && TH.pools.mode===3 ? 0x86c05a : 0xff6a28);
        S.crackD.add(cx, 0, cz, p.scale,p.scale,p.scale, p.rot, vc, dl);
        break; }
      case 'bones':
        S.bone.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, 0xcfc4a4, dl);
        break;
      case 'cargo':
        S.cargo.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, lerpC(TH.wall,0xffffff,0.18), dl);
        S.cargoMark.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, accC, dl+0.1);
        break;
      case 'medBed':
        S.medBed.add(X,0.13,Z, p.scale,p.scale,p.scale, p.rot, lerpC(TH.cap,0xffffff,0.3), dl);
        S.medScreen.add(X,0.13,Z, p.scale,p.scale,p.scale, p.rot, accC, dl+0.1);
        break;
      case 'labTank':
        S.labTank.add(X,0,Z, p.scale,p.scale,p.scale, 0, 0xb8d8f0, dl);
        S.labTankCap.add(X,0,Z, p.scale,p.scale,p.scale, 0, lerpC(TH.pillar,0xffffff,0.15), dl+0.05);
        S.labCore.add(X,0,Z, p.scale,p.scale,p.scale, 0, accC, dl+0.12);
        break;
      case 'planter':
        S.planter.add(X,0,Z, p.scale,1,p.scale, p.rot, lerpC(TH.pillar,0xffffff,0.1), dl);
        S.planterGlow.add(X,0,Z, p.scale,1,p.scale, p.rot, 0x59d68f, dl+0.12);
        break;
      case 'console':
        S.consoleB.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, lerpC(TH.wall,0xffffff,0.22), dl);
        S.consoleScreen.add(X,0,Z, p.scale,p.scale,p.scale, p.rot, accC, dl+0.1);
        break;
      case 'ship':
        S.ship.add(X,0,Z, 1,1,1, p.rot, lerpC(TH.wall,0xffffff,0.25), dl);
        S.shipGlow.add(X,0,Z, 1,1,1, p.rot, accC, dl+0.12);
        break;
      case 'serviceCore':
        S.serviceCore.add(X,0,Z, 1,1,1, p.rot, lerpC(TH.pillar,0xffffff,0.08), dl);
        S.serviceCoreGlow.add(X,0,Z, 1,1,1, p.rot, accC, dl+0.12);
        break;
      case 'banner': {
        const ry = Math.atan2(p.dx, p.dy);
        const bx = wx(p.x)+p.dx*0.54, bz = wz(p.y)+p.dy*0.54;
        const bdl = nearFloorBfs(p.x,p.y)*dStep + 0.7;
        S.bannerRod.add(bx, 1.98, bz, 1,1,1, ry, 0x6a5a3a, bdl);
        S.bannerCloth.add(bx+p.dx*0.03, 1.96, bz+p.dy*0.03, 1,1,1, ry, TH.cloth, bdl+0.05);
        S.emblem.add(bx+p.dx*0.06, 1.6, bz+p.dy*0.06, 1,1,1, ry, accC, bdl+0.1);
        break; }
    }
  }

  /* torches */
  for(const t of d.torches){
    const ry = Math.atan2(t.dx, t.dy);
    const X = wx(t.x)+t.dx*0.5, Z = wz(t.y)+t.dy*0.5, dl = nearFloorBfs(t.x,t.y)*dStep + 0.66;
    S.torchArm.add(X, 1.02, Z, 1,1,1, ry, 0x4a4038, dl);
    S.flame.add(X+t.dx*0.16, 1.5, Z+t.dy*0.16, 1.2,1.2,1.2, 0, TH.flame, dl+0.08);
    S.flameCore.add(X+t.dx*0.16, 1.53, Z+t.dy*0.16, 1.2,1.2,1.2, 0, TH.flameCore, dl+0.08);
  }

  /* d.spawns are now real enemies, instantiated at build time by DungeonArena
     (issue #44), so the decorative spawn-tier markers that used to stand in for
     them are gone — a marker under a live enemy would just be visual noise. */

  /* doorway arches */
  for(const a of d.arches){
    const X=wx(a.x), Z=wz(a.y);
    const half = a.len/2 + 0.15;
    const dlA = nearFloorBfs(Math.round(a.x), Math.round(a.y))*dStep + 0.7;
    const col = lerpC(TH.wall, 0xffffff, 0.12);
    if(a.px===1){
      S.arch.add(X-half,0,Z, 1,1,1, 0, col, dlA);
      S.arch.add(X+half,0,Z, 1,1,1, 0, col, dlA);
      S.archL.add(X,1.62,Z, a.len+0.42,1,1, 0, col, dlA+0.1);
    } else {
      S.arch.add(X,0,Z-half, 1,1,1, 0, col, dlA);
      S.arch.add(X,0,Z+half, 1,1,1, 0, col, dlA);
      S.archL.add(X,1.62,Z, a.len+0.42,1,1, Math.PI/2, col, dlA+0.1);
    }
  }

  /* liquid pockets + frozen lakes */
  if(TH.pools){
    this.mats.liquidMat.uniforms.uMode.value = TH.pools.mode;
    this.mats.liquidMat.uniforms.uColA.value.set(TH.pools.colA);
    this.mats.liquidMat.uniforms.uColB.value.set(TH.pools.colB);
    this.mats.liquidMat.uniforms.uGlow.value = TH.pools.glow;
  }
  if(d.pools.length){
    const skirtC = TH.pools.mode===0 ? 0xff5a1f : (TH.pools.mode===3 ? 0x33531e : 0x11463c);
    for(const p of d.pools){
      const dl = nearFloorBfs(p.x,p.y)*dStep + 0.5;
      S.basin.add(wx(p.x), 0, wz(p.y), 1,1,1, 0, lerpC(TH.wall,0x000000,0.35), dl);
      S.skirt.add(wx(p.x), 0, wz(p.y), cellRng.f(0.85,1.25),1,cellRng.f(0.85,1.25), cellRng.f(0,6.28), skirtC, dl+0.15);
    }
    const m = this.buildLiquidMesh(d.pools, wx, wz, -0.08);
    this.group.add(m); this.fx.liquids.push(m);
  }
  if(d.lakeCells.length){
    const m = this.buildLiquidMesh(d.lakeCells, wx, wz, -0.12);
    this.group.add(m); this.fx.liquids.push(m);
  }

  const setDefs = [
    ['pillar',   this.GEO.pillar,    this.mats.matStone,  'rise', 0.4,  1],
    ['pillarBand', this.GEO.pillarBand, this.mats.matGlow, 'pop', 0.4,  0],
    ['cargo',    this.GEO.cargo,     this.mats.matTrim,   'rise', 0.4,  1],
    ['cargoMark',this.GEO.cargoMark, this.mats.matGlow,   'pop',  0.4,  0],
    ['medBed',   this.GEO.medBed,    this.mats.matTrim,   'pop',  0.35, 1],
    ['medScreen',this.GEO.medScreen, this.mats.matGlow,   'pop',  0.4,  0],
    ['labTank',  this.GEO.labTank,   this.mats.matIce,    'rise', 0.45, 0],
    ['labTankCap', this.GEO.labTankCap, this.mats.matTrim, 'pop', 0.4,  0],
    ['labCore',  this.GEO.labCore,   this.mats.matGlow,   'pop',  0.5,  0],
    ['planter',  this.GEO.planter,   this.mats.matTrim,   'pop',  0.35, 1],
    ['planterGlow', this.GEO.planterGlow, this.mats.matGlow, 'pop', 0.45, 0],
    ['consoleB', this.GEO.console,   this.mats.matTrim,   'pop',  0.35, 1],
    ['consoleScreen', this.GEO.consoleScreen, this.mats.matGlow, 'pop', 0.4, 0],
    ['ship',     this.GEO.ship,      this.mats.matTrim,   'rise', 0.5,  1],
    ['shipGlow', this.GEO.shipGlow,  this.mats.matGlow,   'pop',  0.55, 0],
    ['serviceCore', this.GEO.serviceCore, this.mats.matTrim, 'rise', 0.5, 1],
    ['serviceCoreGlow', this.GEO.serviceCoreGlow, this.mats.matGlow, 'pop', 0.55, 0],
    ['arch',     this.GEO.archPost,  this.mats.matStone,  'rise', 0.45, 1],
    ['archL',    this.GEO.archLintel,this.mats.matStone,  'pop',  0.35, 1],
    ['torchArm', this.GEO.torch,     this.mats.matTrim,   'pop',  0.3,  0],
    ['flame',    this.GEO.flame,     this.mats.matGlow,   'pop',  0.3,  0],
    ['flameCore',this.GEO.flameCore, this.mats.matGlow,   'pop',  0.3,  0],
    ['debrisA',  this.GEO.debrisA,   this.mats.matStone,  'pop',  0.3,  2],
    ['debrisB',  this.GEO.debrisB,   this.mats.matStone,  'pop',  0.3,  2],
    ['debrisC',  this.GEO.debrisC,   this.mats.matStone,  'pop',  0.3,  2],
    ['chest',    this.GEO.chestBody, this.mats.matStone,  'pop',  0.35, 1],
    ['chestTrim',this.GEO.chestTrim, this.mats.matTrim,   'pop',  0.35, 0],
    ['chestGlow',this.GEO.chestSeam, this.mats.matGlow,   'pop',  0.4,  0],
    ['grave',    this.GEO.grave,     this.mats.matStone,  'rise', 0.4,  1],
    ['sarco',    this.GEO.sarco,     this.mats.matStone,  'pop',  0.4,  1],
    ['candle',   this.GEO.candle,    this.mats.matStone,  'pop',  0.3,  0],
    ['bone',     this.GEO.bone,      this.mats.matStone,  'pop',  0.3,  0],
    ['icicle',   this.GEO.icicle,    this.mats.matIce,    'pop',  0.35, 0],
    ['shardIce', this.GEO.shard,     this.mats.matIce,    'pop',  0.35, 0],
    ['roots',    this.GEO.roots,     this.mats.matBark,   'rise', 0.5,  1],
    ['moss',     this.GEO.moss,      this.mats.matMoss,   'pop',  0.4,  0],
    ['crackD',   this.GEO.crack,     this.mats.matCrackD, 'pop',  0.4,  0],
    ['skirt',    this.GEO.skirt,     this.mats.matSkirt,  'pop',  0.5,  0],
    ['bannerRod',this.GEO.bannerRod, this.mats.matTrim,   'pop',  0.3,  0],
    ['bannerCloth',this.GEO.bannerCloth,this.mats.matCloth,'rise',0.4,  0],
    ['emblem',   this.GEO.emblem,    this.mats.matGlow,   'pop',  0.3,  0],
    ['crystal',  this.GEO.crystal,   this.mats.matGlow,   'pop',  0.4,  0],
    ['ring',     this.GEO.ring,      this.mats.matGlow,   'pop',  0.4,  0],
    ['plinth',   this.GEO.plinth,    this.mats.matStone,  'pop',  0.4,  1],
    ['platform', this.GEO.platform,  this.mats.matStone,  'pop',  0.45, 2],
    ['brazier',  this.GEO.brazier,   this.mats.matTrim,   'pop',  0.35, 1],
    ['coals',    this.GEO.coals,     this.mats.matGlow,   'pop',  0.35, 0],
    ['basin',    this.GEO.basin,     this.mats.matStone,  'pop',  0.3,  0],
    ['bossGlow', this.GEO.bossShard, this.mats.matGlow,   'rise', 0.5,  0],
    ['bossRock', this.GEO.bossShard, this.mats.matStone,  'rise', 0.5,  1]
  ];
  for(const [k, geo, mat, mode, dur, sh] of setDefs) this.meshes[k] = buildMesh(S[k], geo, mat, mode, dur, sh);
  for(const k in this.meshes) if (this.meshes[k]) this.group.add(this.meshes[k]);

  /* hero single meshes: portal swirl, boss rune ring, god-ray shafts */
  if(portalXZ){
    this.mats.matPortal.color.set(0x3fd0bb);
    const m = new THREE.Mesh(this.GEO.portal, this.mats.matPortal);
    m.position.set(portalXZ[0], 0.12, portalXZ[1]);
    this.group.add(m); this.fx.spinners.push({m, spd:0.55});
  }
  if(runeXZ){
    this.mats.matRune.color.set(0xff5040);
    const m = new THREE.Mesh(this.GEO.runeRing, this.mats.matRune);
    m.position.set(runeXZ.x, 0.06, runeXZ.z);
    m.scale.setScalar(runeXZ.s);
    this.group.add(m); this.fx.spinners.push({m, spd:-0.16});
  }
  if(TH.shafts){
    const big = rooms.filter(r=>r.type===TYPE.COMBAT && !r.lake).sort((a,b)=>b.w*b.h-a.w*a.h).slice(0,2);
    for(const r of big) shaftAt.push([wx(r.cx), wz(r.cy), 1.3]);
  }
  for(const s of shaftAt){
    const m = new THREE.Mesh(this.GEO.shaft, this.mats.matShaft);
    m.position.set(s[0], 0, s[1]);
    m.scale.setScalar(s[2]);
    this.group.add(m); this.fx.shafts.push(m);
  }

  /* ambient particles — emitted from sources that make physical sense:
     embers off lava + flames, wisps over graves/candles/miasma, spores off
     moss/roots/water, dust inside light shafts, snow as weather everywhere */
  { const spec = TH.particles;
    const pts = [];
    const pp = (x,z,y)=>pts.push({x,z,y});
    if(spec.kind===1){
      for(const p of d.pools) pp(wx(p.x)+cellRng.f(-0.3,0.3), wz(p.y)+cellRng.f(-0.3,0.3), -0.02);
      for(const t of d.torches) pp(wx(t.x)+t.dx*0.66, wz(t.y)+t.dy*0.66, 1.5);
      for(const p of d.props) if(p.kind==='brazier') pp(wx(p.x), wz(p.y), 0.62);
    } else if(spec.kind===3){
      for(const p of d.props){
        if(p.kind==='grave' || p.kind==='sarco') pp(wx(p.x)+cellRng.f(-0.2,0.2), wz(p.y)+cellRng.f(-0.2,0.2), 0.3);
        else if(p.kind==='candle') pp(wx(p.x), wz(p.y), 0.25);
        else if(p.kind==='bones') pp(wx(p.x), wz(p.y), 0.1);
      }
      for(const p of d.pools) pp(wx(p.x), wz(p.y), 0);
    } else if(spec.kind===4){
      for(const p of d.props){
        if(p.kind==='moss') pp(wx(p.x)+cellRng.f(-0.25,0.25), wz(p.y)+cellRng.f(-0.25,0.25), 0.05);
        else if(p.kind==='roots') pp(wx(p.x)+p.dx*0.8, wz(p.y)+p.dy*0.8, cellRng.f(0.2,1.4));
      }
      for(const p of d.pools) pp(wx(p.x), wz(p.y), 0);
    } else if(spec.kind===0){
      for(const s of shaftAt) for(let k=0;k<10;k++)
        pp(s[0]+cellRng.f(-0.8,0.8)*s[2], s[1]+cellRng.f(-0.8,0.8)*s[2], cellRng.f(0.3,2.4));
      for(const t of d.torches) pp(wx(t.x)+t.dx*0.7, wz(t.y)+t.dy*0.7, cellRng.f(1.2,1.9));
    } else {
      for(let y=0;y<H;y++) for(let x=0;x<W;x++)
        if(grid[idx(x,y)]===FLOOR && cellRng.chance(0.25)) pp(wx(x), wz(y), 0);
    }
    if(!pts.length)
      for(let y=0;y<H;y++) for(let x=0;x<W;x++)
        if(grid[idx(x,y)]===FLOOR && cellRng.chance(0.1)) pp(wx(x), wz(y), 0);
    if(pts.length){
      const n = Math.min(spec.n, Math.max(40, pts.length*6));
      const pos = new Float32Array(n*3), seed = new Float32Array(n);
      for(let i=0;i<n;i++){
        const p = pts[cellRng.i(0, pts.length-1)];
        pos[i*3]=p.x; pos[i*3+1]=p.y; pos[i*3+2]=p.z;
        seed[i]=cellRng.raw();
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos,3));
      g.setAttribute('aSeed', new THREE.BufferAttribute(seed,1));
      this.levelGeos.push(g);
      this.mats.partMat.uniforms.uKind.value = spec.kind;
      this.mats.partMat.uniforms.uColor.value.set(spec.color);
      const pm = new THREE.Points(g, this.mats.partMat);
      pm.frustumCulled = false;
      this.group.add(pm); this.fx.parts = pm;
    }
  }

  /* lights: farthest-point sample of torches + key lights */
  const budget = 12;
  const keys = [];
  keys.push({x:rooms[d.entrance].cx, y:rooms[d.entrance].cy, col:0x3fd0bb, i:1.0, dist:13});
  keys.push({x:rooms[d.boss].cx, y:rooms[d.boss].cy, col:0xff4030, i:1.7, dist:17, ry:2.2});
  const shr = rooms.filter(r=>r.type===TYPE.SHRINE);
  if(shr.length) keys.push({x:shr[0].cx, y:shr[0].cy, col:0x6f9dff, i:1.0, dist:12});
  const tb = Math.max(4, budget - keys.length);
  const chosen = [];
  if(d.torches.length){
    chosen.push(d.torches[0]);
    while(chosen.length < Math.min(tb, d.torches.length)){
      let best=null, bd=-1;
      for(const t of d.torches){
        let dm=1e9; for(const c of chosen){ const q=(t.x-c.x)*(t.x-c.x)+(t.y-c.y)*(t.y-c.y); if(q<dm) dm=q; }
        if(dm>bd){ bd=dm; best=t; }
      }
      chosen.push(best);
    }
  }
  let li=0;
  for(const k of keys){
    const L = new THREE.PointLight(k.col, k.i, k.dist, 2);
    L.position.set(wx(k.x), k.ry||1.6, wz(k.y));
    L.userData={base:k.i, ph:li*2.1, ramp:1}; this.group.add(L); this.lights.push(L); li++;
  }
  for(const t of chosen){
    const L = new THREE.PointLight(TH.torchLight[0], TH.torchLight[1], TH.torchLight[2], 2);
    L.position.set(wx(t.x)+t.dx*0.6, 1.7, wz(t.y)+t.dy*0.6);
    L.userData={base:TH.torchLight[1], ph:li*1.7, ramp:1}; this.group.add(L); this.lights.push(L); li++;
  }

  /* graph overlay (hidden during play) */
  this.overlay = new THREE.Group();
  this.overlay.visible = false;
  this.group.add(this.overlay);
  const ov = this.overlay;
  const mkLines = (pairs, color, y, op)=>{
    const pos = new Float32Array(Math.max(pairs.length,1)*6);
    pairs.forEach((e,i)=>{
      pos.set([wx(rooms[e.a].cx), y, wz(rooms[e.a].cy), wx(rooms[e.b].cx), y, wz(rooms[e.b].cy)], i*6);
    });
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(pos,3));
    const m = new THREE.LineBasicMaterial({color, transparent:true, opacity:op, depthTest:false});
    const l = new THREE.LineSegments(g,m); l.renderOrder=5; ov.add(l); return l;
  };
  const delPairs = delaunay(rooms.map(r=>({x:r.cx,y:r.cy}))).map(e=>({a:e[0],b:e[1]}));
  ov.userData = {
    del:  mkLines(delPairs, 0x6a7385, 2.5, 0.13),
    mst:  mkLines(d.edges.filter(e=>!e.isLoop), 0xdfe4f0, 2.6, 0.7),
    loop: mkLines(d.edges.filter(e=>e.isLoop), 0x39d5e0, 2.65, 0.9),
    crit: mkLines(d.edges.filter(e=>e.isCritical), 0xff4d4d, 2.75, 0.95)
  };
  { const pos=new Float32Array(rooms.length*3), col=new Float32Array(rooms.length*3);
    rooms.forEach((r,i)=>{ pos.set([wx(r.cx),2.85,wz(r.cy)],i*3); _c.set(TINT[r.type]); col.set([_c.r,_c.g,_c.b],i*3); });
    const g=new THREE.BufferGeometry();
    g.setAttribute('position',new THREE.BufferAttribute(pos,3));
    g.setAttribute('color',new THREE.BufferAttribute(col,3));
    const pts=new THREE.Points(g,new THREE.PointsMaterial({size:6,sizeAttenuation:false,vertexColors:true,transparent:true,opacity:0.95,depthTest:false}));
    pts.renderOrder=6; ov.add(pts); ov.userData.pts=pts;
  }
  { const pos=new Float32Array(rooms.length*8*3), col=new Float32Array(rooms.length*8*3);
    rooms.forEach((r,i)=>{ _c.set(TINT[r.type]); for(let k=0;k<8;k++) col.set([_c.r,_c.g,_c.b],(i*8+k)*3); });
    const g=new THREE.BufferGeometry();
    g.setAttribute('position',new THREE.BufferAttribute(pos,3));
    g.setAttribute('color',new THREE.BufferAttribute(col,3));
    const m=new THREE.LineBasicMaterial({vertexColors:true,transparent:true,opacity:0.9,depthTest:false});
    const rects=new THREE.LineSegments(g,m); rects.renderOrder=7; ov.add(rects); ov.userData.rects=rects;
  }
  ov.userData.wx=wx; ov.userData.wz=wz;

  // No forge camera/zoom — SPIN uses its own perspective rig.
  // Settle instances and scale into world units.
  for (const k in this.meshes) {
    const m = this.meshes[k];
    if (m) writeInstances(m, Infinity);
  }
  // Cap torch lights for performance
  if (this.lights.length > this.maxTorchLights) {
    const keep = this.lights.slice(0, this.maxTorchLights);
    for (const L of this.lights.slice(this.maxTorchLights)) {
      L.removeFromParent();
      L.dispose?.();
    }
    this.lights = keep;
  }
  this.group.scale.setScalar(this.tileSize);
  this.scene.add(this.group);
  return this.group;
  }

  /**
   * @typedef {object} DestructibleHandle
   * @property {string} meshKey Which instanced mesh holds the prop (`barrel`/`crate`).
   * @property {number} index Instance slot within that mesh.
   * @property {number} rubbleIndex Slot in the rubble mesh, or -1 for none.
   * @property {number} rubbleScale Scale to reveal the rubble instance at.
   */

  /** Builds the shared geometry/materials for destructibles on first use. @private */
  _ensureDestructAssets() {
    if (this._destructGeo) return;
    const barrel = new THREE.CylinderGeometry(0.42, 0.5, 1.2, 12);
    barrel.translate(0, 0.6, 0);
    const crate = new THREE.BoxGeometry(0.95, 0.95, 0.95);
    crate.translate(0, 0.475, 0);
    // Flat, wide disc: rubble is a decal that reads as "something broke here".
    const rubble = new THREE.CylinderGeometry(0.6, 0.72, 0.16, 10);
    rubble.translate(0, 0.08, 0);
    this._destructGeo = { barrel, crate, rubble };
    this._destructMat = {
      barrel: new THREE.MeshStandardMaterial({ color: PALETTE.explosiveBarrel, roughness: 0.72, metalness: 0.05 }),
      crate: new THREE.MeshStandardMaterial({ color: 0x9c7b45, roughness: 0.85, metalness: 0.0 }),
      rubble: new THREE.MeshStandardMaterial({ color: 0x4a4640, roughness: 0.95, metalness: 0.0 })
    };
  }

  /**
   * Mounts destructible props as world-space instanced meshes and returns a
   * handle per prop so the arena can hide a single one when it breaks. Any prop
   * that leaves rubble also gets a rubble instance placed now but scaled to
   * nothing, so revealing it on death is one instance write rather than a
   * re-alloc.
   *
   * @param {{ kind: string, x: number, z: number, rot?: number, scale?: number,
   *           leavesRubble?: boolean }[]} placements World-space props to mount.
   * @returns {DestructibleHandle[]} One handle per placement, in order.
   */
  mountDestructibles(placements) {
    if (!placements || placements.length === 0) return [];
    this._ensureDestructAssets();

    const sets = { barrel: instSet(), crate: instSet(), rubble: instSet() };
    /** @type {DestructibleHandle[]} */
    const handles = [];
    for (const p of placements) {
      const meshKey = p.kind === 'barrel' ? 'barrel' : 'crate';
      const s = sets[meshKey];
      const scale = p.scale ?? 1;
      const rot = p.rot ?? 0;
      s.add(p.x, 0, p.z, scale, scale, scale, rot, 0xffffff, 0);
      /** @type {DestructibleHandle} */
      const handle = { meshKey, index: s.n - 1, rubbleIndex: -1, rubbleScale: 0 };
      if (p.leavesRubble) {
        sets.rubble.add(p.x, 0, p.z, 0.0001, 0.0001, 0.0001, rot, 0xffffff, 0);
        handle.rubbleIndex = sets.rubble.n - 1;
        handle.rubbleScale = scale * 1.05;
      }
      handles.push(handle);
    }

    const group = new THREE.Group();
    group.name = 'Destructibles';
    const meshes = {
      barrel: buildMesh(sets.barrel, this._destructGeo.barrel, this._destructMat.barrel, 'pop', 0.01, 1),
      crate: buildMesh(sets.crate, this._destructGeo.crate, this._destructMat.crate, 'pop', 0.01, 1),
      rubble: buildMesh(sets.rubble, this._destructGeo.rubble, this._destructMat.rubble, 'pop', 0.01, 2)
    };
    for (const k in meshes) {
      writeInstances(meshes[k], Infinity);
      group.add(meshes[k]);
    }
    // World units already: unlike the dungeon group, this is not scaled by tileSize.
    this.scene.add(group);
    this._destruct = { group, meshes };
    return handles;
  }

  /**
   * Hides a broken prop by collapsing its instance to a near-zero scale (never a
   * full mesh rebuild), and reveals its rubble decal if it had one.
   * @param {DestructibleHandle | null} handle Handle returned by {@link mountDestructibles}.
   * @returns {void}
   */
  destroyDestructible(handle) {
    const d = this._destruct;
    if (!d || !handle) return;

    const mesh = d.meshes[handle.meshKey];
    if (mesh) {
      const s = mesh.userData.set;
      s.sx[handle.index] = 0.0001;
      s.sy[handle.index] = 0.0001;
      s.sz[handle.index] = 0.0001;
      writeInstances(mesh, Infinity);
    }

    if (handle.rubbleIndex >= 0) {
      const rm = d.meshes.rubble;
      const rs = rm.userData.set;
      rs.sx[handle.rubbleIndex] = handle.rubbleScale;
      rs.sy[handle.rubbleIndex] = handle.rubbleScale * 0.5;
      rs.sz[handle.rubbleIndex] = handle.rubbleScale;
      writeInstances(rm, Infinity);
    }
  }

  /**
   * Hides an opened chest by collapsing its `chest`/`chestTrim`/`chestGlow`
   * instances to a near-zero scale, the same trick {@link destroyDestructible}
   * uses for a broken prop. The chest meshes live in the tile-scaled dungeon
   * group (`this.meshes`), not the world-scaled destructibles group, since
   * chests are baked at build time rather than spawned as their own entities.
   * @param {number} index Instance slot, matching the order chests were added
   *   in {@link build} (same as the order `DungeonArena` walks `d.props`).
   * @returns {void}
   */
  hideChestAt(index) {
    if (index == null || index < 0) return;
    for (const key of ['chest', 'chestTrim', 'chestGlow']) {
      const mesh = this.meshes[key];
      if (!mesh) continue;
      const s = mesh.userData.set;
      if (index >= s.n) continue;
      s.sx[index] = 0.0001;
      s.sy[index] = 0.0001;
      s.sz[index] = 0.0001;
      writeInstances(mesh, Infinity);
    }
  }

  _clearMeshes() {
    if (this._destruct) {
      this._destruct.group.removeFromParent();
      this._destruct.group.clear();
      this._destruct = null;
    }
    if (this.group) {
      this.group.removeFromParent();
      this.group.traverse((o) => {
        if (o.isInstancedMesh || o.isMesh || o.isPoints) {
          // shared geos/mats disposed separately
        }
      });
      this.group.clear();
      this.group = null;
    }
    for (const L of this.lights) {
      L.removeFromParent();
      L.dispose?.();
    }
    this.lights = [];
    this.lightGroup.clear();
    this.lightGroup.removeFromParent();
    this.meshes = {};
    this.fx = { liquids: [], shafts: [], spinners: [], parts: null };
    this.overlay = null;
    this.levelGeos = [];
    this.D = null;
  }

  dispose() {
    this._clearMeshes();
    this.mats.dispose();
    this._geoKit.dispose();
    if (this._destructGeo) {
      for (const k in this._destructGeo) this._destructGeo[k].dispose();
      this._destructGeo = null;
    }
    if (this._destructMat) {
      for (const k in this._destructMat) this._destructMat[k].dispose();
      this._destructMat = null;
    }
  }

  /** @returns {THREE.Group} root group (scaled by tileSize) */
  get root() { return this.group; }

  update(dt) {
    if (!this.D) return;
    this._elapsed += dt;
    const time = this._elapsed;
    // crystals / spinners
    for (const k of ['crystal']) {
      const cm = this.meshes[k];
      if (!cm) continue;
      const s = cm.userData.set;
      for (let i = 0; i < s.n; i++) {
        _q.setFromAxisAngle(_Y, s.ry[i] + time * 0.9);
        _p.set(s.px[i], s.py[i] + 0.08 * Math.sin(time * 2.1 + i * 1.7), s.pz[i]);
        _s.set(s.sx[i], s.sy[i], s.sz[i]);
        _m.compose(_p, _q, _s);
        cm.setMatrixAt(i, _m);
      }
      cm.instanceMatrix.needsUpdate = true;
    }
    this.mats.liquidMat.uniforms.uTime.value = time;
    this.mats.partMat.uniforms.uTime.value = time;
    this.mats.partMat.uniforms.uZoom.value = 40 * this.tileSize;
    for (const sp of this.fx.spinners) sp.m.rotation.y = time * sp.spd;
    for (const L of this.lights) {
      const ramp = L.userData.ramp === undefined ? 1 : L.userData.ramp;
      L.intensity = L.userData.base * LIGHT_K * ramp * (0.84 + 0.22 * Math.sin(time * 9 + L.userData.ph) * Math.sin(time * 4.7 + L.userData.ph * 1.7));
    }
  }

}
